/**
 * IASubcategoryFilter Component
 * Dropdown filter for the 8 IA subcategories
 */

import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { IA_SUBCATEGORIES } from '@/lib/ia-data-utils';

interface IASubcategoryFilterProps {
  selectedSubcategory: string;
  onSubcategoryChange: (subcategory: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCount?: number;
  totalCount?: number;
}

export function IASubcategoryFilter({
  selectedSubcategory,
  onSubcategoryChange,
  searchQuery,
  onSearchChange,
  selectedCount = 0,
  totalCount = 0,
}: IASubcategoryFilterProps) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-slate-50 rounded-lg border">
      {/* Search Input */}
      <div className="relative flex-1 w-full sm:max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search activities..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 bg-white"
        />
      </div>

      {/* Subcategory Dropdown */}
      <Select value={selectedSubcategory} onValueChange={onSubcategoryChange}>
        <SelectTrigger className="w-full sm:w-[280px] bg-white">
          <SelectValue placeholder="Select subcategory" />
        </SelectTrigger>
        <SelectContent>
          {IA_SUBCATEGORIES.map((subcat) => (
            <SelectItem key={subcat.id} value={subcat.id}>
              {subcat.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Selection Count Badge */}
      {selectedCount > 0 && (
        <Badge variant="secondary" className="whitespace-nowrap">
          {selectedCount} of {totalCount} Selected
        </Badge>
      )}
    </div>
  );
}

/**
 * Subcategory Pills Component
 * Alternative display as horizontal scrollable pills
 */
export function IASubcategoryPills({
  selectedSubcategory,
  onSubcategoryChange,
}: {
  selectedSubcategory: string;
  onSubcategoryChange: (subcategory: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {IA_SUBCATEGORIES.map((subcat) => (
        <button
          key={subcat.id}
          onClick={() => onSubcategoryChange(subcat.id)}
          className={`
            px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors
            ${selectedSubcategory === subcat.id
              ? 'bg-primary text-primary-foreground'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }
          `}
        >
          {subcat.id === 'all' ? subcat.name : subcat.id}
        </button>
      ))}
    </div>
  );
}



